import { EmptyList } from '../emptyList';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchQuery, graphql, useLazyLoadQuery, useRelayEnvironment } from 'react-relay';
import { ErrorMessage } from 'src/shared/globals';
import { T, backofficeUrl, stringFormat } from 'src/shared/helpers';
import { usePantoneColorsCheck } from '../../../shared/hooks/usePantoneColorsCheck';
import { quickSearchDialogResultsMenuQuery } from './__generated__/quickSearchDialogResultsMenuQuery.graphql';
import { quickSearchDialogResultsResultQuery } from './__generated__/quickSearchDialogResultsResultQuery.graphql';
import { DialogResultNameWithHighlight } from './DialogResultNameWithHighlight';
import { DialogResult, DialogResultTitle, DialogResultsGroup } from './DialogResults';
import { QueryDialogResultsLoader } from './queryDialogResultsLoader';
import { DialogResultItem } from './quickSearchDialog';
import searchMenu from './quickSearchMenuSearcher';

type DialogResultCategory = {
    name: string;
    results: DialogResultItem[];
};

const categoriesToOpenInNewTab = ['Documentation'] as const;

export const QuickSearchDialogResults = ({ filter }: { filter: string }) => {
    const [resultsCategories, setResultsCategories] = useState<DialogResultCategory[]>([]);
    const [selectedResult, setSelectedResult] = useState<string | null>(null);
    const [fetchingStatus, setFetchingStatus] = useState({ isLoading: false, isError: false });
    const relayEnvironment = useRelayEnvironment();
    const filterRef = useRef(filter);
    const { hasPantoneColors } = usePantoneColorsCheck();

    const shouldHideResult = (url?: string) => {
        if (!hasPantoneColors && url?.toLowerCase().includes('pantone')) {
            return true;
        }

        return false;
    };

    const query = useLazyLoadQuery<quickSearchDialogResultsMenuQuery>(
        graphql`
            query quickSearchDialogResultsMenuQuery {
                treeMenu
            }
        `,
        {}
    );

    filterRef.current = filter;

    const allResultsItems = resultsCategories.flatMap((x) => x.results);

    if (allResultsItems.length > 0 && !selectedResult) {
        setSelectedResult(allResultsItems[0].url);
    }

    useEffect(() => {
        setSelectedResult(null);
        setResultsCategories([]);

        if (filter === '') return;

        (async () => {
            const menuResults = await searchInMenu();
            setResultsCategories(menuResults);

            const dbResults = await searchInDatabase();

            // Merge and filter unwanted results
            const merged = [...menuResults, ...dbResults].map((cat) => ({
                ...cat,
                results: cat.results.filter((r) => !shouldHideResult(r.url))
            }));

            setResultsCategories(merged.filter((c) => c.results.length > 0));
        })();
    }, [filter]);

    // Search for menu items locally
    const searchInMenu = async () => {
        // First search for menu items locally
        const searchResult = await searchMenu(filterRef.current.trim(), JSON.parse(query.treeMenu));
        const filteredResults = searchResult.filter((x) => !shouldHideResult(x.url));
        const menuResults =
            filteredResults.length > 0
                ? [
                      {
                          name: T._('Menu', 'Admin'),
                          results: filteredResults.map((x) => ({
                              name: x.title,
                              url: x.url,
                              icon: ''
                          }))
                      }
                  ]
                : [];

        return menuResults;
    };

    const searchInDatabase = async () => {
        let dbResults: DialogResultCategory[] = [];

        try {
            setFetchingStatus({
                isLoading: true,
                isError: false
            });

            dbResults = await new Promise<DialogResultCategory[]>((resolve, reject) => {
                searchInDatabaseDebounced(resolve, reject);
            });

            setFetchingStatus({
                isLoading: false,
                isError: false
            });
        } catch (e) {
            setFetchingStatus({
                isLoading: false,
                isError: true
            });
        }

        return dbResults;
    };

    // Fetch results from GraphQL API for products, orders, etc.
    const searchInDatabaseDebounced = useCallback(
        debounce(async (onComplete: (results: DialogResultCategory[]) => void, onError: () => void) => {
            try {
                let results: DialogResultCategory[] = [];

                const queryResult = await fetchQuery<quickSearchDialogResultsResultQuery>(
                    relayEnvironment,
                    graphql`
                        query quickSearchDialogResultsResultQuery($filter: String!) {
                            quickSearch(filter: $filter) {
                                name
                                results {
                                    title
                                    url
                                }
                            }
                        }
                    `,
                    { filter: filterRef.current.trim() }
                ).toPromise();

                if (queryResult) {
                    results = queryResult.quickSearch
                        .filter((x) => x.results.length > 0)
                        .map((x) => ({
                            name: x.name,
                            results: x.results.map((x) => ({
                                name: x.title,
                                url: x.url,
                                icon: ''
                            }))
                        }));
                }

                onComplete(results);
            } catch (e) {
                console.error(e);
                onError();
            }
        }, 300),
        []
    );

    // Move the selected result with up and down arrows
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                e.stopImmediatePropagation();
                const index = allResultsItems.findIndex((x) => x.url === selectedResult);
                if (index < allResultsItems.length - 1) {
                    setSelectedResult(allResultsItems[index + 1].url);
                }
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                e.stopImmediatePropagation();
                const index = allResultsItems.findIndex((x) => x.url === selectedResult);
                if (index > 0) {
                    setSelectedResult(allResultsItems[index - 1].url);
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [resultsCategories, selectedResult]);

    // On enter key, go to the selected result
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (selectedResult && e.key === 'Enter') {
                e.preventDefault();
                e.stopImmediatePropagation();
                const isUrlAbsolute = selectedResult.indexOf('http') === 0;
                const newUrl = isUrlAbsolute ? selectedResult : backofficeUrl(selectedResult);
                const openInNewTab = categoriesToOpenInNewTab.some((x) => resultsCategories.some((y) => y.name === x));

                if (openInNewTab) window.open(newUrl, '_blank');
                else window.location.href = newUrl;
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedResult]);

    return (
        <>
            {resultsCategories.map((x) => (
                <DialogResultsGroup>
                    <DialogResultTitle>{x.name}</DialogResultTitle>
                    {x.results.map((result) => {
                        const isUrlAbsolute = result.url.indexOf('http') === 0;
                        const newUrl = isUrlAbsolute ? result.url : backofficeUrl(result.url);

                        return (
                            <DialogResult
                                data-track={`quick search result click ${x.name} > ${result.name}`}
                                key={result.url}
                                href={newUrl}
                                target={categoriesToOpenInNewTab.includes(x.name as (typeof categoriesToOpenInNewTab)[number]) ? '_blank' : '_self'}
                                aria-selected={selectedResult === result.url ? true : false}
                                onMouseEnter={() => setSelectedResult(result.url)}
                            >
                                <DialogResultNameWithHighlight name={result.name} highlight={filter.trim()} />
                            </DialogResult>
                        );
                    })}
                </DialogResultsGroup>
            ))}

            {/* Empty results */}
            {resultsCategories.length === 0 && filter != '' && !fetchingStatus.isError && !fetchingStatus.isLoading && (
                <EmptyList title={stringFormat(T._('No results found for "{0}"', 'Admin'), filter)} subTitle={'Try to search for something else.'} />
            )}

            {/* Fetch loading */}
            {fetchingStatus.isLoading && <QueryDialogResultsLoader />}

            {/* Fetch error */}
            {fetchingStatus.isError && <ErrorMessage>{T._('Error fetching search results.', 'Admin')}</ErrorMessage>}
        </>
    );
};
